import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { kpService } from '../../services/api';
import BirthDataForm from '../vedic/forms/BirthDataForm';
import { to24Hour } from '../vedic/forms/TimeInput';
import ErrorAlert from '../vedic/feedback/ErrorAlert';
import LoadingState from '../vedic/feedback/LoadingState';
import ResponsiveTable from '../vedic/results/ResponsiveTable';

const PageContainer = styled.div`max-width:1200px;margin:40px auto;padding:0 20px;`;
const PageTitle = styled.h1`font-size:2rem;text-align:center;margin-bottom:10px;color:${p => p.theme.colors.text.primary};`;
const PageSubtitle = styled.p`text-align:center;color:${p => p.theme.colors.text.secondary};margin-bottom:30px;font-size:0.95rem;`;
const Card = styled.div`background:${p => p.theme.colors.surface};border-radius: ${p => p.theme.radius.md}px;box-shadow: ${p => p.theme.shadow.card};padding:30px;margin-bottom:24px;`;
const CardTitle = styled.h2`font-size:1.3rem;margin-bottom:20px;color:${p => p.theme.colors.text.primary};text-align:center;`;
const FormGroup = styled.div`margin-bottom:18px;`;
const Label = styled.label`display:block;margin-bottom:6px;font-weight:500;color:${p => p.theme.colors.text.primary};font-size:0.95rem;`;
const Input = styled.input`width:100%;padding:10px 14px;border:1px solid ${p => p.theme.colors.border};border-radius: ${p => p.theme.radius.sm}px;font-size:1rem;box-sizing:border-box;&:focus{outline:none;border-color:#f9d71c;}`;
const Select = styled.select`width:100%;padding:10px 14px;border:1px solid ${p => p.theme.colors.border};border-radius: ${p => p.theme.radius.sm}px;font-size:1rem;background:${p => p.theme.colors.surface};box-sizing:border-box;&:focus{outline:none;border-color:#f9d71c;}`;
const Row = styled.div`display:grid;grid-template-columns:${p => p.cols || '1fr 1fr'};gap:12px;@media(max-width:576px){grid-template-columns:1fr;}`;
const SubmitBtn = styled.button`
  background:#f9d71c;color:${p => p.theme.colors.text.primary};border:none;border-radius: ${p => p.theme.radius.sm}px;padding:12px 20px;
  font-size:1rem;font-weight:600;cursor:pointer;width:100%;margin-top:8px;
  &:hover{background:#e6c519}&:disabled{opacity:0.6;cursor:not-allowed}
`;
const BackBtn = styled.button`background:none;border:1px solid ${p => p.theme.colors.border};color:${p => p.theme.colors.text.secondary};padding:8px 20px;border-radius:20px;cursor:pointer;font-size:0.85rem;margin-bottom:16px;&:hover{border-color:#f9d71c;color:${p => p.theme.colors.text.primary}}`;
const Badge = styled.span`display:inline-block;background:${p=>p.bg||'#e3f2fd'};color:${p=>p.color||'#1565c0'};padding:2px 8px;border-radius:10px;font-size:0.75rem;font-weight:600;margin:2px;`;
const SectionTitle = styled.h3`font-size:1.1rem;color:${p => p.theme.colors.text.secondary};margin:20px 0 10px;border-bottom:2px solid #f9d71c;padding-bottom:6px;`;
const TabRow = styled.div`display:flex;gap:8px;margin-bottom:20px;flex-wrap:wrap;justify-content:center;`;
const Tab = styled.button`padding:8px 16px;border-radius:20px;border:1px solid ${p=>p.active?'#f9d71c':p.theme.colors.border};background:${p=>p.active?'#f9d71c':p.theme.colors.surface};color:${p => p.theme.colors.text.primary};cursor:pointer;font-weight:${p=>p.active?'600':'400'};font-size:0.85rem;&:hover{border-color:#f9d71c}`;

const KPChart = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('natal');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [chart, setChart] = useState(null);
  const [prediction, setPrediction] = useState(null);

  // Natal mode uses shared BirthDataForm
  const [natalValues, setNatalValues] = useState({
    name: '', dob: '', hour: '', minute: '', period: 'AM', timeFormat: '24h',
    place: '', latitude: null, longitude: null, timezone: 'Asia/Kolkata',
  });

  // Horary/predict modes keep inline form
  const [form, setForm] = useState({
    name: '', date: '', time: '', place: '', lat: '', lon: '',
    kp_number: '', question: '', event_type: '',
  });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleNatalSubmit = async (submitValues) => {
    setError(''); setChart(null); setPrediction(null); setLoading(true);
    try {
      const dobParts = (submitValues.dob || '').split('-');
      const hour24 = to24Hour(
        parseInt(submitValues.hour || '0'),
        submitValues.period || 'AM',
        submitValues.timeFormat || '24h'
      );
      const timeStr = `${String(hour24).padStart(2,'0')}:${String(parseInt(submitValues.minute||'0')).padStart(2,'0')}`;
      const res = await kpService.generateChart({
        name: submitValues.name, date: submitValues.dob, time: timeStr,
        place: submitValues.place,
        lat: submitValues.latitude || 0, lon: submitValues.longitude || 0,
      });
      setChart(res.data.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setChart(null); setPrediction(null); setLoading(true);
    try {
      let res;
      if (mode === 'horary') {
        res = await kpService.generateHorary({
          kp_number: parseInt(form.kp_number, 10),
          question: form.question, event_type: form.event_type,
          lat: parseFloat(form.lat || '0'), lon: parseFloat(form.lon || '0'),
          place: form.place,
        });
        setChart(res.data.data);
        if (res.data.prediction) setPrediction(res.data.prediction);
      } else {
        res = await kpService.predictEvent({
          name: form.name, date: form.date, time: form.time,
          place: form.place, lat: parseFloat(form.lat), lon: parseFloat(form.lon),
          event_type: form.event_type, question: form.question,
        });
        setChart(res.data.data);
        setPrediction(res.data.prediction);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <BackBtn onClick={() => navigate('/vedic-tools')}>&larr; Back to Tools</BackBtn>
      <PageTitle>KP Stellar System</PageTitle>
      <PageSubtitle>Krishnamurti Paddhati — Sub-Lord based precision prediction</PageSubtitle>

      <TabRow>
        <Tab active={mode==='natal'} onClick={()=>setMode('natal')}>Natal Chart</Tab>
        <Tab active={mode==='horary'} onClick={()=>setMode('horary')}>Horary (1-249)</Tab>
        <Tab active={mode==='predict'} onClick={()=>setMode('predict')}>Event Prediction</Tab>
      </TabRow>

      <Card>
        <CardTitle>{mode === 'natal' ? 'Generate KP Natal Chart' : mode === 'horary' ? 'KP Horary Chart' : 'KP Event Prediction'}</CardTitle>
        <ErrorAlert message={error} onDismiss={() => setError('')} />

        {mode === 'natal' && (
          <BirthDataForm
            fields={['name', 'dob', 'time', 'place']}
            values={natalValues}
            onChange={setNatalValues}
            onSubmit={handleNatalSubmit}
            submitLabel={loading ? 'Calculating...' : 'Generate Chart'}
            loading={loading}
            pageName="kpChart"
          />
        )}

        {/* TODO: Migrate horary and predict mode forms to shared components */}
        {mode === 'horary' && (
          <form onSubmit={handleSubmit}>
            <FormGroup><Label>KP Number (1-249) *</Label><Input name="kp_number" type="number" min="1" max="249" value={form.kp_number} onChange={handleChange} required placeholder="Ask the querent to pick a number" /></FormGroup>
            <FormGroup><Label>Question</Label><Input name="question" value={form.question} onChange={handleChange} placeholder="e.g. Will I get the job?" /></FormGroup>
            <FormGroup><Label>Place</Label><Input name="place" value={form.place} onChange={handleChange} placeholder="e.g. Chennai" /></FormGroup>
            <Row>
              <FormGroup><Label>Latitude</Label><Input name="lat" type="number" step="any" value={form.lat} onChange={handleChange} placeholder="e.g. 13.0827" /></FormGroup>
              <FormGroup><Label>Longitude</Label><Input name="lon" type="number" step="any" value={form.lon} onChange={handleChange} placeholder="e.g. 80.2707" /></FormGroup>
            </Row>
            <FormGroup>
              <Label>Event Type</Label>
              <Select name="event_type" value={form.event_type} onChange={handleChange}>
                <option value="">-- Select --</option>
                <option value="marriage">Marriage</option>
                <option value="career">Career / Job</option>
                <option value="education">Education</option>
                <option value="children">Children</option>
                <option value="property">Property Purchase</option>
                <option value="foreign_travel">Foreign Travel</option>
                <option value="health">Health Recovery</option>
                <option value="legal">Legal Victory</option>
                <option value="money">Money / Income</option>
                <option value="promotion">Promotion</option>
                <option value="business">Business Profit</option>
                <option value="loan">Loan Approval</option>
                <option value="vehicle">Vehicle Purchase</option>
              </Select>
            </FormGroup>
            <SubmitBtn type="submit" disabled={loading}>{loading ? 'Calculating...' : 'Generate Chart'}</SubmitBtn>
          </form>
        )}

        {mode === 'predict' && (
          <form onSubmit={handleSubmit}>
            <FormGroup><Label>Name</Label><Input name="name" value={form.name} onChange={handleChange} placeholder="Enter name" /></FormGroup>
            <Row>
              <FormGroup><Label>Date of Birth *</Label><Input name="date" type="date" value={form.date} onChange={handleChange} required /></FormGroup>
              <FormGroup><Label>Time of Birth *</Label><Input name="time" type="time" value={form.time} onChange={handleChange} required /></FormGroup>
            </Row>
            <FormGroup><Label>Place of Birth</Label><Input name="place" value={form.place} onChange={handleChange} placeholder="e.g. Madurai" /></FormGroup>
            <Row>
              <FormGroup><Label>Latitude *</Label><Input name="lat" type="number" step="any" value={form.lat} onChange={handleChange} required placeholder="e.g. 9.9252" /></FormGroup>
              <FormGroup><Label>Longitude *</Label><Input name="lon" type="number" step="any" value={form.lon} onChange={handleChange} required placeholder="e.g. 78.1198" /></FormGroup>
            </Row>
            <FormGroup>
              <Label>Event Type *</Label>
              <Select name="event_type" value={form.event_type} onChange={handleChange} required>
                <option value="">-- Select --</option>
                <option value="marriage">Marriage</option>
                <option value="career">Career / Job</option>
                <option value="education">Education</option>
                <option value="children">Children</option>
                <option value="property">Property Purchase</option>
                <option value="foreign_travel">Foreign Travel</option>
                <option value="health">Health Recovery</option>
                <option value="legal">Legal Victory</option>
                <option value="money">Money / Income</option>
                <option value="promotion">Promotion</option>
                <option value="business">Business Profit</option>
                <option value="loan">Loan Approval</option>
                <option value="vehicle">Vehicle Purchase</option>
              </Select>
            </FormGroup>
            <FormGroup><Label>Question</Label><Input name="question" value={form.question} onChange={handleChange} placeholder="Optional — describe the question" /></FormGroup>
            <SubmitBtn type="submit" disabled={loading}>{loading ? 'Calculating...' : 'Predict'}</SubmitBtn>
          </form>
        )}
      </Card>

      {chart && <ChartResults chart={chart} />}
      {prediction && <PredictionResults prediction={prediction} />}
    </PageContainer>
  );
};

const ChartResults = ({ chart }) => (
  <>
    <Card>
      <CardTitle>KP Chart — {chart.type === 'horary' ? `Horary #${chart.kp_number}` : (chart.name || 'Natal')}</CardTitle>
      <div style={{display:'flex',justifyContent:'center',gap:24,flexWrap:'wrap',fontSize:'0.9rem',color:'#555',marginBottom:16}}>
        <span><strong>Date/Time:</strong> {chart.date_time}</span>
        {chart.location?.place && <span><strong>Place:</strong> {chart.location.place}</span>}
        <span><strong>Ayanamsa:</strong> {chart.ayanamsa_used} ({chart.ayanamsa_value?.toFixed(4)}°)</span>
        <span><strong>Ascendant:</strong> {chart.ascendant?.toFixed(2)}°</span>
      </div>
      {chart.current_dasha && (
        <div style={{textAlign:'center',marginBottom:16}}>
          <Badge bg="#fff3e0" color="#e65100">Dasha: {chart.current_dasha}</Badge>
          <Badge bg="#e8f5e9" color="#2e7d32">Bhukti: {chart.current_bhukti}</Badge>
          <Badge bg="#e3f2fd" color="#1565c0">Antara: {chart.current_antara}</Badge>
        </div>
      )}
    </Card>

    {chart.planet_table && chart.planet_table.length > 0 && (
      <Card>
        <SectionTitle>Planet Table (Sign / Star / Sub)</SectionTitle>
        <ResponsiveTable
          compact
          columns={[
            {key:'planet',label:'Planet'},{key:'longitude',label:'Long°'},{key:'sign',label:'Sign'},
            {key:'sign_lord',label:'Sign Lord'},{key:'star_lord',label:'Star Lord'},
            {key:'sub_lord',label:'Sub Lord'},{key:'sub_sub_lord',label:'Sub-Sub'},
            {key:'house',label:'House'},{key:'retrograde',label:'R'},
          ]}
          rows={chart.planet_table.map(p => ({
            ...p,
            longitude: p.longitude?.toFixed(2),
            retrograde: p.is_retrograde,
          }))}
        />
      </Card>
    )}

    {chart.cusp_table && chart.cusp_table.length > 0 && (
      <Card>
        <SectionTitle>Cusp Table (Bhav Spasht)</SectionTitle>
        <ResponsiveTable
          compact
          columns={[
            {key:'house',label:'House'},{key:'cusp_degree',label:'Cusp°'},{key:'sign',label:'Sign'},
            {key:'sign_lord',label:'Sign Lord'},{key:'star_lord',label:'Star Lord'},
            {key:'sub_lord',label:'Sub Lord'},{key:'sub_sub_lord',label:'Sub-Sub'},
            {key:'house_span',label:'Span°'},
          ]}
          rows={chart.cusp_table.map(c => ({
            ...c,
            cusp_degree: c.cusp_degree?.toFixed(2),
            house_span: c.house_span?.toFixed(1),
          }))}
        />
      </Card>
    )}

    {chart.significator_table?.entries && Object.keys(chart.significator_table.entries).length > 0 && (
      <Card>
        <SectionTitle>Significator Table</SectionTitle>
        <ResponsiveTable
          compact
          columns={[
            {key:'planet',label:'Planet'},{key:'l1',label:'L1: Star Occ.'},
            {key:'l2',label:'L2: Star Own.'},{key:'l3',label:'L3: Self Occ.'},
            {key:'l4',label:'L4: Self Own.'},{key:'all',label:'All Houses'},
          ]}
          rows={Object.values(chart.significator_table.entries).map(s => ({
            planet: s.planet,
            l1: (s.level1_star_lord_occupied_houses||[]).join(', '),
            l2: (s.level2_star_lord_owned_houses||[]).join(', '),
            l3: (s.level3_self_occupied_houses||[]).join(', '),
            l4: (s.level4_self_owned_houses||[]).join(', '),
            all: (s.all_signified_houses||[]).join(', '),
          }))}
        />
      </Card>
    )}

    {chart.ruling_planets && (
      <Card>
        <SectionTitle>Ruling Planets</SectionTitle>
        <div style={{display:'flex',justifyContent:'center',gap:16,flexWrap:'wrap',fontSize:'0.9rem'}}>
          <span><strong>Vara:</strong> {chart.ruling_planets.vara_lord}</span>
          <span><strong>Moon Sign:</strong> {chart.ruling_planets.moon_sign_lord}</span>
          <span><strong>Moon Star:</strong> {chart.ruling_planets.moon_star_lord}</span>
          <span><strong>Asc Sign:</strong> {chart.ruling_planets.asc_sign_lord}</span>
          <span><strong>Asc Star:</strong> {chart.ruling_planets.asc_star_lord}</span>
        </div>
        <div style={{textAlign:'center',marginTop:12}}>
          {(chart.ruling_planets.all_rp||[]).map((rp, i) => <Badge key={i} bg="#f3e5f5" color="#7b1fa2">{rp}</Badge>)}
        </div>
      </Card>
    )}
  </>
);

const PredictionResults = ({ prediction }) => {
  const confColor = prediction.confidence_color || '#757575';
  const confBg = confColor + '20';
  const confLabel = {
    strong_yes: 'STRONG YES', weak_yes: 'LIKELY YES',
    uncertain: 'UNCERTAIN', weak_no: 'LIKELY NO', strong_no: 'STRONG NO'
  }[prediction.confidence] || 'UNCERTAIN';

  return (
    <Card>
      <SectionTitle>KP Prediction — {prediction.event_type}</SectionTitle>
      {prediction.question && <p style={{textAlign:'center',color:'#555',fontStyle:'italic',marginBottom:16}}>"{prediction.question}"</p>}
      <div style={{textAlign:'center',marginBottom:16}}>
        <Badge bg={confBg} color={confColor} style={{fontSize:'1rem',padding:'6px 16px'}}>{confLabel}</Badge>
      </div>
      <div style={{fontSize:'0.9rem',color:'#555',marginBottom:12}}>
        <p><strong>Primary Cusp:</strong> House {prediction.primary_cusp} | <strong>Sub-Lord:</strong> {prediction.cusp_sub_lord}</p>
        <p><strong>Sub-Lord signifies houses:</strong> {(prediction.sub_lord_signifies||[]).join(', ')}</p>
        <p><strong>Favorable:</strong> {(prediction.favorable_houses||[]).join(', ')} | <strong>Unfavorable:</strong> {(prediction.unfavorable_houses||[]).join(', ')}</p>
      </div>
      {prediction.reasoning && prediction.reasoning.length > 0 && (
        <div style={{background:'#f5f5f5',padding:12,borderRadius:6,fontSize:'0.85rem',color:'#555'}}>
          <strong>Reasoning:</strong>
          <ul style={{margin:'4px 0',paddingLeft:20}}>{prediction.reasoning.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </div>
      )}
    </Card>
  );
};

export default KPChart;
